import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  GET_APP_BOOTSTRAP,
  type AppBootstrapData,
  type BootstrapProgress,
} from 'src/api/queries/getAppBootstrap';

// Polling cadence for the BUILDING-zero-state banner. Tier 1 promotions
// land in batches; 30s strikes a balance between responsiveness and
// per-tab traffic. Polling stops once the company hits ``ready``.
const POLL_INTERVAL_MS = 30_000;

interface UseBootstrapProgressOptions {
  companyId: string | null | undefined;
}

interface UseBootstrapProgressResult {
  bootstrapProgress: BootstrapProgress | null;
  loading: boolean;
}

/**
 * Reads the selected company's ``bootstrapProgress`` from the unified
 * bootstrap query and polls every 30s while the company is in the
 * ``building`` phase. Polling halts as soon as phase flips to ``ready``
 * (or anything other than ``building``).
 *
 * Shares the Apollo cache with ``AppBootstrapProvider``'s initial
 * imperative query — the first read is typically a cache hit.
 */
export function useBootstrapProgress({
  companyId,
}: UseBootstrapProgressOptions): UseBootstrapProgressResult {
  const { data, loading, startPolling, stopPolling } = useQuery<{
    getAppBootstrap: AppBootstrapData;
  }>(GET_APP_BOOTSTRAP, {
    variables: { companyId: companyId ?? null },
    skip: !companyId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
  });

  const bootstrapProgress = data?.getAppBootstrap?.selectedCompany?.bootstrapProgress ?? null;
  const phase = bootstrapProgress?.phase ?? null;

  useEffect(() => {
    if (!companyId) return;
    if (phase === 'building') {
      startPolling(POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [phase, companyId, startPolling, stopPolling]);

  return { bootstrapProgress, loading };
}
